import React, {useState} from 'react';
import {actionApiCall, rffFormSubmit, WaitForQueryData} from '@mc/react-app-v4';
import {
    Block,
    Button,
    Callout,
    Card,
    CheckStatus,
    Dialog,
    DialogBody,
    DialogFooter,
    LabelValue,
    List,
    ListHeader,
    ListItem,
    Modal,
    ModalComponentProps,
    Space,
    Text,
    Title,
    VF,
} from '@mc/ui-v4';
import {getExportFormatItems} from '../../enums/ExportFormats';
import {getDataFilterExportDownloadUrl} from '@mc/data-filter-react';
import {Form, FormRenderProps} from 'react-final-form';
import {LayerVO, ExportImportApi, PublicExportApi, ExportFormat} from '../../api';
import {SERVER_CONTEXT_PATH} from '../../const';
import {useLayersConfig} from '../../hooks/useLayersConfig';
import {set} from 'lodash-es';
import {useMapioContext} from '../../mapioContext';
import {useAppConfig} from '@mc/react-app-base';
import {LayerId} from '@mc/react-mapio';
import {ApiCallWaitingModal} from './ApiCallWaitingModal';
import {downloadFile} from '../../helpers/FileDownload';
import './ExportVrstevModal.scss';

export const ExportVrstevModal: React.FC<ModalComponentProps> = ({onClose}) => {
    const [disabled, setDisabled] = useState(false);
    const [checked, setChecked] = useState<Record<string, boolean>>({});
    const {context, activeLayers} = useMapioContext().value;
    //@ts-ignore
    const {serverPublic} = useAppConfig();
    const formats = getExportFormatItems(serverPublic);

    const toggleLayer = (layerId: LayerId) => {
        let checkedCopy = JSON.parse(JSON.stringify(checked));
        if (checked[layerId]) {
            delete checkedCopy[layerId];
        } else {
            checkedCopy[layerId] = true;
        }
        setChecked(checkedCopy);
    };

    const layersConfigQuery = useLayersConfig();

    // Vyfiltruje exportovatelné vrstvy z aktivních vrstev (včetně doplnění chybějících rodičovských vrstev).
    const getExportableLayers = (): LayerVO[] => {
        const data = layersConfigQuery.data;
        if (!data) {
            return [];
        }
        // všechny exportovatelné vybrané vrstvy, které nemají parenta
        const vrstvy = Object.values(data).filter(layer => layer.exportable && layer.parentLayerId === null && activeLayers.includes(layer.id));
        // všechny vybrané vrstvy s parentem - přidáme jejich parenty, pokud ještě nejsou v seznamu
        for (const layer of Object.values(data)) {
            if (!(layer.parentLayerId !== null && activeLayers.includes(layer.id))) {
                continue;
            }
            const parentLayer = data[layer.parentLayerId];
            if (parentLayer && parentLayer.exportable && !vrstvy.find(l => l.id === parentLayer.id)) {
                vrstvy.push(parentLayer);
            }
        }
        return vrstvy;
    };

    // Oblast pro export formulář
    const handleFormSubmit = React.useCallback(
        async (data: any) => {
            setDisabled(true);
            const layerIds = Object.keys(checked).map(Number);
            const isXlsx = data.format?.toLowerCase() === ExportFormat.XLSX.toLowerCase();
            let result: string;
            if (serverPublic) {
                if (!isXlsx) {
                    throw new Error(`Nepodporovaný formát exportu: ${data.format}`);
                }
                result = await Modal.showWaiting(
                    actionApiCall(async () => (await PublicExportApi.exportToExcel({layerIds}, context)).data),
                    props => <ApiCallWaitingModal {...props} title={'Probíhá export'} />,
                );
            } else if (isXlsx) {
                // XLSX se neexportuje přes GDAL – server ho složí stejným mechanismem jako export
                // atributové tabulky (data-filter) a vrátí ID dočasného souboru ke stažení
                const exportFileId = await Modal.showWaiting(
                    actionApiCall(async () => (await ExportImportApi.exportLayersToExcel(layerIds, context)).data),
                    props => <ApiCallWaitingModal {...props} title={'Probíhá export do XLS'} />,
                );
                result = SERVER_CONTEXT_PATH + getDataFilterExportDownloadUrl(exportFileId);
            } else {
                result =
                    SERVER_CONTEXT_PATH +
                    (await Modal.showWaiting(
                        actionApiCall(async () => (await ExportImportApi.exportLayerFile(data.format, context, layerIds)).data),
                        props => <ApiCallWaitingModal {...props} title={'Probíhá export'} />,
                    ));
            }
            downloadFile(result);
            onClose();
        },
        [serverPublic, checked, context, onClose],
    );

    return (
        <Form
            validate={async data => {
                const errors = {};
                if (!data.format) {
                    set(errors, 'format', 'Vyberte formát exportu');
                }
                if (Object.keys(checked)?.length < 1) {
                    set(errors, 'checked', 'Vyberte alespoň jednu vrstvu');
                }
                return errors;
            }}
            onSubmit={data => rffFormSubmit(() => handleFormSubmit(data).finally(() => setDisabled(false)))}
            initialValues={{
                format: formats.length === 1 ? formats[0].id : undefined,
            }}
        >
            {({submitting, handleSubmit, errors, submitFailed}: FormRenderProps) => {
                const vrstvy = getExportableLayers();
                const checkedAll = vrstvy.map(item => item.id);
                const checkedAllObj = checkedAll.reduce((o, key) => ({...o, [key]: true}), {});

                const checkedItems = Object.keys(checked);
                let checkedStatus = CheckStatus.ALL_UNCHECKED;
                if (checkedItems.length === checkedAll.length && checkedAll.length > 0) {
                    checkedStatus = CheckStatus.ALL_CHECKED;
                } else if (checkedItems.length > 0) {
                    checkedStatus = CheckStatus.SOME_CHECKED;
                }

                return (
                    <Dialog size={'md'} onClose={onClose} title={'Export vrstev'} autoFocus={false}>
                        <DialogBody>
                            <WaitForQueryData queries={[layersConfigQuery]}>
                                {() => {
                                    if (vrstvy.length === 0) {
                                        return <Text>Export není podporován žádnou z aktivních vrstev.</Text>;
                                    }
                                    return (
                                        <Card mode={'edit'}>
                                            <Block>
                                                <LabelValue label={'Vrstvy k exportu'}>
                                                    <ListHeader
                                                        checkable
                                                        totalElements={vrstvy.length}
                                                        checkedStatus={checkedStatus}
                                                        onToggleCheckAll={() => {
                                                            if (Object.keys(checked).length === checkedAll.length) {
                                                                setChecked({});
                                                            } else {
                                                                setChecked(checkedAllObj);
                                                            }
                                                        }}
                                                    />
                                                    {errors?.checked && submitFailed && <Callout intent={'danger'}>{errors.checked}</Callout>}
                                                    <List mode={'view'} compact className={'export-layer-list'}>
                                                        {vrstvy.map((item: LayerVO) => (
                                                            <ListItem
                                                                key={item.id}
                                                                rowCheck
                                                                checkable
                                                                onCheck={toggleLayer.bind(null, item.id)}
                                                                checked={!!checked[item?.id]}
                                                                disabled={submitting}
                                                            >
                                                                {item.label}
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </LabelValue>
                                                <VF.SelectBox
                                                    name={'format'}
                                                    required
                                                    label={'Formát'}
                                                    items={formats}
                                                    readOnly={formats.length === 1}
                                                    disabled={submitting}
                                                    useIdAsValue
                                                />
                                            </Block>
                                        </Card>
                                    );
                                }}
                            </WaitForQueryData>
                        </DialogBody>
                        <DialogFooter>
                            <Button style={'default'} onClick={() => onClose()} disabled={submitting}>
                                Zrušit
                            </Button>
                            {vrstvy.length > 0 && (
                                <Button style={'primary'} disabled={disabled} onClick={handleSubmit}>
                                    Exportovat
                                </Button>
                            )}
                        </DialogFooter>
                    </Dialog>
                );
            }}
        </Form>
    );
};
